import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { caseStudies } from '../data/projectCaseStudies';

const categoryColors = {
  'Healthcare & Vision': { bg: 'rgba(139,31,47,0.12)', text: 'hsl(350,62%,33%)', border: 'rgba(139,31,47,0.25)', accent: 'hsl(350,62%,33%)' },
  'AI Platforms':        { bg: 'rgba(49,82,103,0.12)', text: 'hsl(203,36%,30%)', border: 'rgba(49,82,103,0.25)', accent: 'hsl(203,36%,34%)' },
  'Automation & Agents': { bg: 'rgba(104,90,52,0.13)', text: 'hsl(44,34%,29%)',  border: 'rgba(104,90,52,0.26)', accent: 'hsl(44,34%,34%)' },
  'Research':            { bg: 'rgba(88,24,35,0.12)',  text: 'hsl(350,55%,24%)', border: 'rgba(88,24,35,0.25)',  accent: 'hsl(350,55%,27%)' },
};

// Per-project hero visual: icon + accent gradient identity for each case study
const heroIcons = {
  'mbot':         { icon: 'ri-microscope-line',     gradient: 'linear-gradient(135deg, var(--first-color), var(--profile-blue))' },
  'osteodoc':     { icon: 'ri-pulse-line',          gradient: 'linear-gradient(135deg, var(--profile-oxblood), var(--first-color))' },
  'content-mod':  { icon: 'ri-shield-check-line',   gradient: 'linear-gradient(135deg, var(--first-color), var(--profile-blue))' },
  'campfire':     { icon: 'ri-fire-line',           gradient: 'linear-gradient(135deg, var(--profile-copper), var(--first-color))' },
  'gluon':        { icon: 'ri-broadcast-line',      gradient: 'linear-gradient(135deg, var(--profile-blue), var(--first-color))' },
  'kris':         { icon: 'ri-mic-fill',            gradient: 'linear-gradient(135deg, var(--profile-blue), var(--profile-oxblood))' },
  'ifs':          { icon: 'ri-mental-health-line',  gradient: 'linear-gradient(135deg, var(--profile-oxblood), var(--profile-blue))' },
  'qr':           { icon: 'ri-qr-code-line',        gradient: 'linear-gradient(135deg, var(--profile-blue), var(--profile-charcoal))' },
  'tenders':      { icon: 'ri-file-search-line',    gradient: 'linear-gradient(135deg, var(--profile-olive), var(--profile-blue))' },
  'apgym':        { icon: 'ri-plant-line',          gradient: 'linear-gradient(135deg, var(--profile-olive), var(--profile-copper))' },
  'visionmap':    { icon: 'ri-film-line',           gradient: 'linear-gradient(135deg, var(--profile-blue), var(--first-color))' },
  'thesis':       { icon: 'ri-book-2-line',         gradient: 'linear-gradient(135deg, var(--profile-oxblood), var(--profile-blue))' },
};

const NAV_SECTIONS = [
  { id: 'cs-challenge',   label: 'The Challenge' },
  { id: 'cs-role',        label: 'My Contribution' },
  { id: 'cs-solution',    label: 'What Was Built' },
  { id: 'cs-process',     label: 'How It Came Together' },
  { id: 'cs-stack',       label: 'The Stack' },
  { id: 'cs-outcomes',    label: 'Outcomes' },
  { id: 'cs-reflection',  label: 'What I\'d Refine' },
];

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const cs = caseStudies.find(c => c.id === id);

  const [activeSection, setActiveSection] = useState('cs-challenge');
  const observerRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    if (!cs) return;

    const sections = NAV_SECTIONS.map(s => document.getElementById(s.id)).filter(Boolean);

    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );

    sections.forEach(s => observerRef.current.observe(s));
    return () => observerRef.current && observerRef.current.disconnect();
  }, [cs]);

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!cs) {
    return (
      <div className="case-study__not-found container">
        <h1>Project not found</h1>
        <p>No case study exists for this project ID.</p>
        <button className="case-study__back-link" onClick={() => navigate('/#projects')}>
          ← All Work
        </button>
      </div>
    );
  }

  const col = categoryColors[cs.category] || categoryColors['AI Platforms'];
  const hero = heroIcons[cs.id] || { icon: 'ri-stack-line', gradient: 'linear-gradient(135deg, var(--profile-blue), var(--first-color))' };

  // Related projects: 3 from same category (excluding current), then fill from other categories
  const sameCategory = caseStudies.filter(c => c.category === cs.category && c.id !== cs.id);
  const otherCategory = caseStudies.filter(c => c.category !== cs.category && c.id !== cs.id);
  const related = [...sameCategory, ...otherCategory].slice(0, 3);

  return (
    <div className="case-study__page">

      {/* ── Back link ──────────────────────────────────────── */}
      <div className="container">
        <button
          className="case-study__back-link"
          onClick={() => navigate('/#projects')}
        >
          <i className="ri-arrow-left-line"></i> All Work
        </button>
      </div>

      {/* ── Hero ───────────────────────────────────────────── */}
      <header className="case-study__hero">
        <div className="case-study__hero-glow" aria-hidden="true"></div>
        <div className="container">
          <div className="case-study__hero-grid">
            {/* Text column */}
            <div className="case-study__hero-text">
              <span
                className="case-study__category-badge"
                style={{ background: col.bg, color: col.text, border: `1px solid ${col.border}` }}
              >
                {cs.category}
              </span>
              <h1 className="case-study__hero-title">{cs.title}</h1>
              <p className="case-study__hero-tagline">{cs.tagline}</p>

              {cs.stats && cs.stats.length > 0 && (
                <div className="case-study__stats">
                  {cs.stats.map((stat, idx) => (
                    <div className="case-study__stat" key={stat.label} style={{ animationDelay: `${idx * 90}ms` }}>
                      <span className="case-study__stat-n">{stat.n}</span>
                      <span className="case-study__stat-label">{stat.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Visual column — per-project icon hero */}
            <div className="case-study__hero-visual" aria-hidden="true">
              <div className="case-study__hero-card" style={{ background: hero.gradient }}>
                <i className={`case-study__hero-icon ${hero.icon}`}></i>
                <div className="case-study__hero-card-meta">
                  <span className="case-study__hero-card-id">{cs.id}</span>
                  <span className="case-study__hero-card-cat">{cs.category}</span>
                </div>
                <div className="case-study__hero-card-grid"></div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── Main layout: content + sticky nav ─────────────── */}
      <div className="container">
        <div className="case-study__layout">

          {/* Main content */}
          <main className="case-study__main">

            <section id="cs-challenge" className="case-study__section">
              <p className="case-study__section-heading">The Challenge</p>
              <p className="case-study__prose">{cs.problem}</p>
            </section>

            <section id="cs-role" className="case-study__section">
              <p className="case-study__section-heading">My Contribution</p>
              <p className="case-study__prose">{cs.role}</p>
            </section>

            <section id="cs-solution" className="case-study__section">
              <p className="case-study__section-heading">What Was Built</p>
              <p className="case-study__prose">{cs.solution}</p>
            </section>

            <section id="cs-process" className="case-study__section">
              <p className="case-study__section-heading">How It Came Together</p>
              <p className="case-study__prose">{cs.process}</p>
            </section>

            <section id="cs-stack" className="case-study__section">
              <p className="case-study__section-heading">The Stack</p>
              <div className="case-study__stack-grid">
                {cs.stack.map(item => (
                  <div className="case-study__stack-card" key={item.name}>
                    <span className="case-study__tech-name">{item.name}</span>
                    <span className="case-study__tech-reason">{item.reason}</span>
                  </div>
                ))}
              </div>
            </section>

            <section id="cs-outcomes" className="case-study__section">
              <p className="case-study__section-heading">Outcomes</p>
              <p className="case-study__prose">{cs.outcomes}</p>
            </section>

            <section id="cs-reflection" className="case-study__section">
              <p className="case-study__section-heading">What I'd Refine</p>
              <p className="case-study__prose">{cs.reflection}</p>
            </section>

            {/* CTA Footer */}
            <div className="case-study__cta">
              <p className="case-study__cta-heading">Interested in this kind of work?</p>
              <p className="case-study__cta-sub">Let's talk about what you're building.</p>
              <button
                className="case-study__cta-btn"
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    const el = document.getElementById('contact');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
              >
                <i className="ri-mail-send-line"></i> Get in touch
              </button>
              <div className="case-study__cta-back">
                <button className="case-study__back-link" onClick={() => navigate('/#projects')}>
                  <i className="ri-arrow-left-line"></i> Back to all projects
                </button>
              </div>
            </div>

          </main>

          {/* Sticky sidebar nav */}
          <aside className="cs-nav__sidebar">
            <p className="cs-nav__label">On this page</p>
            <ul className="cs-nav__list">
              {NAV_SECTIONS.map(s => (
                <li key={s.id}>
                  <button
                    className={`cs-nav__link${activeSection === s.id ? ' cs-nav__link--active' : ''}`}
                    onClick={() => scrollToSection(s.id)}
                  >
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

        </div>
      </div>

      {/* ── Related projects ──────────────────────────────── */}
      {related.length > 0 && (
        <section className="case-study__related">
          <div className="container">
            <p className="case-study__related-eyebrow">Continue exploring</p>
            <h2 className="case-study__related-heading">Related work</h2>
            <div className="case-study__related-grid">
              {related.map(r => {
                const rCol  = categoryColors[r.category] || categoryColors['AI Platforms'];
                const rHero = heroIcons[r.id] || { icon: 'ri-stack-line', gradient: 'linear-gradient(135deg, var(--profile-blue), var(--first-color))' };
                return (
                  <Link to={`/projects/${r.id}`} className="case-study__related-card" key={r.id}>
                    <div className="case-study__related-thumb" style={{ background: rHero.gradient }}>
                      <i className={`case-study__related-icon ${rHero.icon}`}></i>
                    </div>
                    <div className="case-study__related-body">
                      <span
                        className="case-study__related-cat"
                        style={{ background: rCol.bg, color: rCol.text, border: `1px solid ${rCol.border}` }}
                      >
                        {r.category}
                      </span>
                      <h3 className="case-study__related-title">{r.title}</h3>
                      <p className="case-study__related-tagline">{r.tagline}</p>
                      <span className="case-study__related-cta">
                        Read case study <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProjectDetail;
